/**
 * Staff turnover, employees first.
 *
 * The window is the financial year to date that the turnover block carries itself. It does
 * not follow the period picker, and says so on its own face.
 *
 * Three readings this panel protects:
 *
 *  1. EMPLOYEES LEAD ON EVERY BASIS. The headline is Permanent + FTC. Every engaged worker's
 *     exits sit beside it on the wider bases and are never added into it. There is no
 *     all-categories rate: Permanent and FTC rates are over people, ICA and Casual rates over
 *     filled positions, and the two do not add.
 *  2. NULL IS WITHHELD, ABSENT IS NOT AVAILABLE. A count withheld to protect a small group
 *     arrives as null. It shows as a dash with the rule attached, is never drawn as 0 and is
 *     never worked back from the figures beside it. A key the response leaves out is not
 *     available for this date.
 *  3. LEAVERS SET APART ARE PEOPLE WHO LEFT. People who left on a contract extension (CE) or
 *     renewal (CR) code are kept out of the exits, as the Remco data pack keeps them out, and
 *     shown beside the rate. They are not movement inside the workforce.
 *
 * A copy stored before the daily recalculation still serves: it carries an all-categories
 * figure, a flat list of reasons and no per-block date stamp, and is read as it is.
 */
import React, { useMemo, type ReactNode } from 'react';
import { BarRow } from './BarRow';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  CATEGORY_COLORS,
  CATEGORY_ORDER,
  Def,
  SESSIONAL_CATEGORIES,
  categoryLabel,
  fmtDay,
  fmtNum,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type ReasonSource,
  type WorkforceBasis,
  type WorkforcePanels,
  type WorkforceWindow,
} from './apiContract';

interface TurnoverPanelProps {
  panels: WorkforcePanels | null;
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

const REASON_COLOR = '#5B6B85';

const SUPPRESSION_NOTE =
  'Withheld: fewer than 5 people, or a figure that would let a smaller one elsewhere on this page '
  + 'be worked out. It is not zero, and it is still in the totals.';

const WITHHELD_SUB = 'withheld to protect small groups';
const NOT_AVAILABLE_SUB = 'not available for this date';
const SET_APART_SUB = 'left on a contract extension or renewal code · not counted as exits';

const SOURCE_LABELS: Record<ReasonSource, string> = {
  dynamics_fo: 'held in Dynamics F&O',
  sage_payroll: 'recovered from Sage payroll',
  not_recorded: 'not recorded',
};

/** Panel-local definitions. Anything /metric-definitions publishes for the same key wins. */
const OWN_DEFS: Record<string, ReactNode> = {
  turnover_employees: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Employee turnover</p>
      <p>
        Permanent and fixed-term people who left during the window, as a percentage of the
        average number of employees in post at the window&apos;s start and end.
      </p>
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>Not annualised: it is the rate for the window shown on the panel.</li>
      </ul>
    </div>
  ),
  turnover_by_category: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Turnover by category</p>
      <p>
        Exits and rate for each employment category. Permanent and fixed-term rates are over
        people; independent contractor and casual rates are over filled positions.
      </p>
    </div>
  ),
};

/** A withheld figure: a dash with the rule attached, never 0. */
const Withheld: React.FC<{ def: DefLookup }> = ({ def }) => (
  <span className="inline-flex items-center justify-end text-gray-400">
    —<span className="sr-only">withheld</span>
    <Def def={def} k="suppression" text={SUPPRESSION_NOTE} />
  </span>
);

const NotAvailable: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="rounded border border-gray-200 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-600">
    {children}
  </div>
);

interface TileFigure {
  value: ReactNode;
  sub: string;
}

/** null: withheld. undefined: the response does not carry it for this date. */
function figure(n: number | null | undefined, sub: string, def: DefLookup): TileFigure {
  if (n === undefined) return { value: '—', sub: NOT_AVAILABLE_SUB };
  if (n === null) return { value: <Withheld def={def} />, sub: WITHHELD_SUB };
  return { value: fmtNum(n), sub };
}

/**
 * The words that date this panel, from the block's own provenance entry, never the frame's.
 * A block stamped with a kind reads through panelDateLabel. A copy stored before blocks were
 * stamped carries no kind, and panelDateLabel would call its window a point in time, so it is
 * dated by the window it carries, or the day it runs to. null: nothing to date it by.
 */
function dateWords(
  panels: WorkforcePanels | null,
  block: string,
  own?: WorkforceWindow | null,
): string | null {
  const entry = panels?.panels_provenance?.[block];
  if (!entry?.computed_for) return null;
  if (entry.kind) return panelDateLabel(panels, block).replace(/^To /, 'to ');
  if (own?.from && own.to) return `${fmtDay(own.from)} – ${fmtDay(own.to)}`;
  return `to ${fmtDay(entry.computed_for)}`;
}

export const TurnoverPanel: React.FC<TurnoverPanelProps> = ({ panels, basis, loading, def }) => {
  const defs = useMemo<DefLookup>(() => (k: string) => def(k) ?? OWN_DEFS[k], [def]);
  const employeeView = basis === 'employees';

  const turnover = panels?.turnover ?? null;
  const extra = readExtras(panels, 'turnover');
  const analysis = readExtras(panels, 'exit_analysis');
  const setApart = readExtras(panels, 'conversions');

  const emp = turnover?.employees_only ?? null;
  const overall = turnover?.overall;
  const categories = turnover?.by_category ?? {};
  const catKeys = [
    ...CATEGORY_ORDER.filter((k) => k in categories),
    ...Object.keys(categories).filter((k) => !CATEGORY_ORDER.includes(k)),
  ];
  const hasTurnover = !!turnover && (!!emp || !!overall || catKeys.length > 0);

  // ── headline: employees on every basis ─────────────────────────────────────
  const empRate: TileFigure = !emp
    ? { value: '—', sub: NOT_AVAILABLE_SUB }
    : emp.pct !== null
      ? { value: fmtPct(emp.pct), sub: `of ${fmtNum(emp.avg)} employees in post on average` }
      : emp.suppressed || emp.leavers === null
        ? { value: <Withheld def={defs} />, sub: WITHHELD_SUB }
        : { value: '—', sub: 'nobody in post over the window, so no rate' };
  const empExits = figure(emp ? emp.leavers : undefined, 'Permanent and fixed-term', defs);
  // Every engaged worker's exits, beside the employees and never added to them. Only a copy
  // stored before the daily recalculation carries `overall`.
  const allExits = extra
    ? figure(extra.all_engaged_leavers, 'every engaged worker', defs)
    : overall
      ? figure(
          overall.leavers,
          overall.pct === null
            ? 'every engaged worker'
            : `every engaged worker · ${fmtPct(overall.pct)} of filled positions`,
          defs,
        )
      : figure(undefined, '', defs);
  // `extras.conversions` absent and the count null: not carried for this date, not withheld.
  const setApartTile = figure(
    setApart ? setApart.leavers : panels?.conversions ?? undefined,
    SET_APART_SUB,
    defs,
  );
  const extensionCoded = setApart?.extension_coded_exits;

  // ── rate by category, each on its own base ─────────────────────────────────
  const maxPct = Math.max(
    0,
    ...catKeys.map((k) => categories[k].pct).filter((v): v is number => typeof v === 'number'),
  );
  const categoryGroups = [
    { id: 'people', heading: 'Over people in post', keys: catKeys.filter((k) => categories[k].basis === 'people') },
    {
      id: 'filled_positions',
      heading: 'Over filled positions',
      keys: catKeys.filter((k) => categories[k].basis === 'filled_positions'),
    },
    // A copy stored before each cell stated its base: no base is claimed for it.
    { id: 'unstated', heading: null, keys: catKeys.filter((k) => !categories[k].basis) },
  ].filter((g) => g.keys.length);
  const hasBasis = catKeys.some((k) => !!categories[k].basis);
  const unclassified = extra?.unclassified_leavers;

  // ── exit reasons: employees on the employee basis, every engaged worker otherwise ──
  // A withheld reason arrives as null. It keeps its row and its place, shows as withheld, and
  // never sizes a bar.
  const exitReasons = panels?.exit_reasons ?? {};
  const reasonKeys = Object.keys(exitReasons);
  const order = analysis?.reason_order ?? [];
  const orderedReasons = order.length
    ? [...order.filter((r) => r in exitReasons), ...reasonKeys.filter((r) => !order.includes(r))]
    : [...reasonKeys].sort((a, b) => (exitReasons[b] ?? -1) - (exitReasons[a] ?? -1));
  const maxReason = Math.max(
    0,
    ...reasonKeys.map((r) => exitReasons[r]).filter((v): v is number => typeof v === 'number'),
  );
  const withheldReasons = reasonKeys.filter((r) => exitReasons[r] === null).length;
  const payrollUnchecked = analysis?.sage_payroll_checked === false;
  const scope = analysis ? (employeeView ? analysis.employees : analysis.all_engaged) : null;
  const sourceText =
    scope && !payrollUnchecked
      ? (Object.keys(SOURCE_LABELS) as ReasonSource[])
          .filter((k) => k in scope.sources)
          .map((k) => {
            const n = scope.sources[k];
            return `${SOURCE_LABELS[k]} ${typeof n === 'number' ? fmtNum(n) : 'withheld'}`;
          })
          .join(' · ')
      : '';
  const foHeld = scope?.sources.dynamics_fo;
  const uncheckedDetail =
    !scope || foHeld === undefined
      ? ''
      : foHeld === null || scope.leavers === null
        ? ' How many of these exits Dynamics F&O holds a reason for is withheld to protect small groups.'
        : ` Dynamics F&O holds a reason for ${fmtNum(foHeld)} of ${fmtNum(scope.leavers)} exits.`;

  const dated = dateWords(panels, 'turnover', panels?.window);
  const windowNote = `Financial year to date${dated ? `, ${dated}` : ''}, not annualised. ${
    extra
      ? 'Recalculated each day; the period picker does not change it.'
      : hasTurnover
        ? 'Computed once over this window; the period picker does not change it.'
        : 'The period picker does not change it.'
  }`;

  const footnote = (
    <>
      {basis === 'positions'
        ? 'A position cannot leave, so on the positions basis the exits are counted for every engaged worker, beside the employees. '
        : ''}
      {hasBasis
        ? 'Permanent and fixed-term rates are over people; independent contractor and casual rates are over filled positions, which reflects how they are engaged rather than attrition. '
        : ''}
      People who left on a contract extension or renewal code are not counted as exits, as in the
      Remco data pack; they are shown beside the rate. Fixed-term turnover is dominated by contract
      end rather than resignation, so read the exit reasons alongside the rate.
    </>
  );

  return (
    <PanelShell
      title="Staff turnover"
      defKey="turnover"
      def={defs}
      window={windowNote}
      footnote={hasTurnover ? footnote : undefined}
    >
      {loading && !panels ? (
        <PanelSkeleton height={260} />
      ) : !panels ? (
        <PanelUnavailable what="Turnover" />
      ) : !hasTurnover ? (
        <NotAvailable>Staff turnover is not available for this date.</NotAvailable>
      ) : (
        <div className="space-y-4">
          <div>
            <TileRow min="140px">
              <MetricTile
                label="Employee turnover"
                value={empRate.value}
                sub={empRate.sub}
                defKey="turnover_employees"
                def={defs}
                size="sm"
              />
              <MetricTile
                label="Employee exits"
                value={empExits.value}
                sub={empExits.sub}
                defKey="leavers"
                def={defs}
                size="sm"
              />
              {employeeView ? null : (
                <MetricTile
                  label="All exits"
                  value={allExits.value}
                  sub={allExits.sub}
                  defKey="leavers"
                  def={defs}
                  size="sm"
                />
              )}
              <MetricTile
                label="Leavers set apart"
                value={setApartTile.value}
                sub={setApartTile.sub}
                defKey="conversions"
                def={defs}
                size="sm"
              />
            </TileRow>
            {typeof extensionCoded === 'number' && extensionCoded > 0 ? (
              <p className="pt-1.5 text-[11px] leading-snug text-slate-500">
                A further {fmtNum(extensionCoded)} who left on other contract-extension codes (a date
                or rate change) are counted as exits, under End of contract.
              </p>
            ) : extensionCoded === null ? (
              <p className="pt-1.5 text-[11px] leading-snug text-slate-500">
                People who left on other contract-extension codes (a date or rate change) are counted
                as exits, under End of contract; how many is withheld to protect small groups.
              </p>
            ) : null}
          </div>

          {categoryGroups.length ? (
            <div>
              <div className="mb-1 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Rate by category
                <Def def={defs} k="turnover_by_category" />
              </div>
              {categoryGroups.map((g) => (
                <div key={g.id} className={g.heading ? 'pt-1' : undefined}>
                  {g.heading ? <div className="text-[11px] text-gray-500">{g.heading}</div> : null}
                  {g.keys.map((k) => {
                    const cell = categories[k];
                    const withheld = cell.suppressed || cell.leavers === null;
                    return (
                      <BarRow
                        key={k}
                        label={
                          withheld
                            ? `${categoryLabel(k)} · exits withheld`
                            : `${categoryLabel(k)} · ${fmtNum(cell.leavers)} exits`
                        }
                        value={cell.pct}
                        max={maxPct}
                        color={CATEGORY_COLORS[k] ?? '#B9C4D6'}
                        faded={employeeView && SESSIONAL_CATEGORIES.includes(k)}
                        valueText={
                          cell.pct !== null ? (
                            fmtPct(cell.pct)
                          ) : withheld ? (
                            <Withheld def={defs} />
                          ) : (
                            <span className="text-gray-400">no rate</span>
                          )
                        }
                      />
                    );
                  })}
                </div>
              ))}
              {unclassified === null ? (
                <p className="pt-1 text-[11px] text-slate-400">
                  Exits from positions with no contract type recorded are counted in every engaged
                  worker&apos;s exits but not rated; how many is withheld to protect small groups.
                </p>
              ) : typeof unclassified === 'number' && unclassified > 0 ? (
                <p className="pt-1 text-[11px] text-slate-400">
                  {fmtNum(unclassified)} exits from positions with no contract type recorded are
                  counted in every engaged worker&apos;s exits but not rated.
                </p>
              ) : null}
            </div>
          ) : null}

          {payrollUnchecked || orderedReasons.length ? (
            <div>
              <div className="mb-1 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Exit reasons · {employeeView ? 'employees' : 'every engaged worker'}
                <Def def={defs} k="exit_reasons" />
              </div>
              {payrollUnchecked ? (
                <p className="text-[12px] leading-snug text-gray-600">
                  Exit reasons are not shown for this date: payroll&apos;s record of why people left
                  could not be checked.{uncheckedDetail}
                </p>
              ) : (
                <>
                  {orderedReasons.map((name) => {
                    const count = exitReasons[name];
                    return (
                      <BarRow
                        key={name}
                        label={name}
                        value={count}
                        max={maxReason}
                        color={REASON_COLOR}
                        labelWidth={200}
                        valueText={count === null ? <Withheld def={defs} /> : undefined}
                      />
                    );
                  })}
                  {sourceText ? (
                    <p className="pt-1 text-[11px] text-slate-400">
                      Where the reason came from: {sourceText}.
                    </p>
                  ) : null}
                  {withheldReasons ? (
                    <p className="pt-1 text-[11px] text-slate-400">
                      {withheldReasons} reason{withheldReasons === 1 ? '' : 's'} withheld — fewer than
                      five people, or a figure that would let one be worked out. They are still in the
                      totals above.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};
